/**
 * Freeform quad helpers for ground decals (skewed pads, perspective patches).
 * Corners are local offsets from the decal center, in world-normalized units.
 * Order: top-left → top-right → bottom-right → bottom-left.
 */

import type { SpawnPoint } from '@/world-prototype/types';
import {
  normalizeRotationDeg,
  rotateOffsets,
} from '@/world-prototype/objects/propTransform';

export type QuadCorners = readonly [
  SpawnPoint,
  SpawnPoint,
  SpawnPoint,
  SpawnPoint,
];

/** Axis-aligned rectangle corners from half extents. */
export function rectQuadCorners(
  halfWidth: number,
  halfHeight: number,
): QuadCorners {
  const hw = Math.max(0.001, halfWidth);
  const hh = Math.max(0.001, halfHeight);
  return [
    { x: -hw, y: -hh },
    { x: hw, y: -hh },
    { x: hw, y: hh },
    { x: -hw, y: hh },
  ];
}

export function cloneQuadCorners(
  corners: readonly SpawnPoint[],
): SpawnPoint[] {
  return corners.map((p) => ({ x: p.x, y: p.y }));
}

export function isQuadCorners(
  corners: readonly SpawnPoint[] | null | undefined,
): corners is QuadCorners {
  return Boolean(corners && corners.length === 4);
}

/** Bounding box of local corners (unrotated). */
export function quadLocalBounds(corners: readonly SpawnPoint[]): {
  width: number;
  height: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of corners) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(0.001, maxX - minX),
    height: Math.max(0.001, maxY - minY),
  };
}

/**
 * Center-anchored element size that fully contains `corners`.
 * Asymmetric quads can extend past ±bounds.width/2 from the local origin;
 * without this, CSS backgrounds stop at the box edge and clip-path points
 * past 100% have no painted pixels (posts appear to sit on grass).
 */
export function quadContainSize(corners: readonly SpawnPoint[]): {
  width: number;
  height: number;
} {
  const b = quadLocalBounds(corners);
  return {
    width: Math.max(
      0.001,
      b.width,
      2 * Math.max(Math.abs(b.minX), Math.abs(b.maxX)),
    ),
    height: Math.max(
      0.001,
      b.height,
      2 * Math.max(Math.abs(b.minY), Math.abs(b.maxY)),
    ),
  };
}

/**
 * CSS clip-path polygon percentages for a center-anchored element whose
 * width/height match `bounds` of the (rotated) local corners.
 */
export function quadClipPathPercent(
  cornersLocalRotated: readonly SpawnPoint[],
  width: number,
  height: number,
): string {
  const w = Math.max(0.001, width);
  const h = Math.max(0.001, height);
  return cornersLocalRotated
    .map((p) => {
      const px = (0.5 + p.x / w) * 100;
      const py = (0.5 + p.y / h) * 100;
      return `${px.toFixed(3)}% ${py.toFixed(3)}%`;
    })
    .join(', ');
}

/**
 * Soft-rounded freeform quad clip-path. Approximates rounded corners by
 * inserting arc samples so skewed pads keep gentle corners like the art.
 *
 * @param cornerRadiusFrac fraction of min(width, height) used as corner radius
 * @param roundCornerMask which corners (TL=0,TR=1,BR=2,BL=3) get rounded.
 *        Default: all four. Pass `[true,true,false,false]` for top-only.
 */
export function roundedQuadClipPathPercent(
  cornersLocalRotated: readonly SpawnPoint[],
  width: number,
  height: number,
  cornerRadiusFrac = 0.07,
  samplesPerCorner = 5,
  roundCornerMask: readonly [boolean, boolean, boolean, boolean] = [
    true,
    true,
    true,
    true,
  ],
): string {
  if (cornersLocalRotated.length < 3) {
    return quadClipPathPercent(cornersLocalRotated, width, height);
  }
  const w = Math.max(0.001, width);
  const h = Math.max(0.001, height);
  const radius = Math.max(0.002, Math.min(w, h) * Math.max(0, cornerRadiusFrac));
  const n = cornersLocalRotated.length;
  const pts: SpawnPoint[] = [];

  for (let i = 0; i < n; i++) {
    const prev = cornersLocalRotated[(i - 1 + n) % n]!;
    const curr = cornersLocalRotated[i]!;
    const next = cornersLocalRotated[(i + 1) % n]!;
    const roundThis = roundCornerMask[i % 4] === true;

    if (!roundThis) {
      pts.push({ x: curr.x, y: curr.y });
      continue;
    }

    const toPrev = { x: prev.x - curr.x, y: prev.y - curr.y };
    const toNext = { x: next.x - curr.x, y: next.y - curr.y };
    const lenPrev = Math.hypot(toPrev.x, toPrev.y) || 1;
    const lenNext = Math.hypot(toNext.x, toNext.y) || 1;
    const rPrev = Math.min(radius, lenPrev * 0.45);
    const rNext = Math.min(radius, lenNext * 0.45);

    const start = {
      x: curr.x + (toPrev.x / lenPrev) * rPrev,
      y: curr.y + (toPrev.y / lenPrev) * rPrev,
    };
    const end = {
      x: curr.x + (toNext.x / lenNext) * rNext,
      y: curr.y + (toNext.y / lenNext) * rNext,
    };

    pts.push(start);
    for (let s = 1; s <= samplesPerCorner; s++) {
      const t = s / (samplesPerCorner + 1);
      // Quadratic Bezier: start → curr → end
      const omt = 1 - t;
      pts.push({
        x: omt * omt * start.x + 2 * omt * t * curr.x + t * t * end.x,
        y: omt * omt * start.y + 2 * omt * t * curr.y + t * t * end.y,
      });
    }
    pts.push(end);
  }

  return pts
    .map((p) => {
      const px = (0.5 + p.x / w) * 100;
      const py = (0.5 + p.y / h) * 100;
      return `${px.toFixed(3)}% ${py.toFixed(3)}%`;
    })
    .join(', ');
}

/** Rounded top corners only (TL + TR); bottom corners stay square. */
export function roundedTopQuadClipPathPercent(
  cornersLocalRotated: readonly SpawnPoint[],
  width: number,
  height: number,
  cornerRadiusFrac = 0.1,
  samplesPerCorner = 5,
): string {
  return roundedQuadClipPathPercent(
    cornersLocalRotated,
    width,
    height,
    cornerRadiusFrac,
    samplesPerCorner,
    [true, true, false, false],
  );
}

export function rotateQuadCorners(
  corners: readonly SpawnPoint[],
  rotationDeg: number,
  worldAspect: number,
): SpawnPoint[] {
  return rotateOffsets(
    corners,
    normalizeRotationDeg(rotationDeg),
    worldAspect,
  );
}

/** Scale all corner offsets from the origin (uniform resize). */
export function scaleQuadCorners(
  corners: readonly SpawnPoint[],
  factor: number,
): QuadCorners {
  const f = Math.max(0.05, factor);
  const next = corners.map((p) => ({ x: p.x * f, y: p.y * f }));
  return [next[0]!, next[1]!, next[2]!, next[3]!];
}

/** Non-uniform scale of corner offsets (independent width / height). */
export function scaleQuadCornersXY(
  corners: readonly SpawnPoint[],
  scaleX: number,
  scaleY: number,
): QuadCorners {
  const sx = Math.max(0.05, scaleX);
  const sy = Math.max(0.05, scaleY);
  const next = corners.map((p) => ({ x: p.x * sx, y: p.y * sy }));
  return [next[0]!, next[1]!, next[2]!, next[3]!];
}
